import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  createToolingSet,
  deleteToolingSet,
  getToolingSet,
  listToolingSets,
  updateToolingSet,
  type ListToolingSetsParams,
  type PaginatedToolingSets,
  type ToolingSet,
  type ToolingSetKey,
  type ToolingSetWriteBody,
} from "../api/toolingSets";

export function useToolingSets(params: ListToolingSetsParams) {
  return useQuery<PaginatedToolingSets>({
    queryKey: ["tooling-sets", "list", params],
    queryFn: () => listToolingSets(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useToolingSet(key: ToolingSetKey | null) {
  return useQuery<ToolingSet>({
    queryKey: ["tooling-sets", "detail", key?.toolingSetId, key?.machineType],
    queryFn: () => getToolingSet(key as ToolingSetKey),
    enabled: key !== null,
    staleTime: 30_000,
  });
}

export function useCreateToolingSet() {
  const queryClient = useQueryClient();
  return useMutation<ToolingSet, Error, ToolingSetWriteBody>({
    mutationFn: (body) => createToolingSet(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tooling-sets"] });
    },
  });
}

export function useUpdateToolingSet() {
  const queryClient = useQueryClient();
  return useMutation<ToolingSet, Error, { oldKey: ToolingSetKey; body: ToolingSetWriteBody }>({
    mutationFn: ({ oldKey, body }) => updateToolingSet(oldKey, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tooling-sets"] });
    },
  });
}

export function useDeleteToolingSet() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, ToolingSetKey>({
    mutationFn: (key) => deleteToolingSet(key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tooling-sets"] });
    },
  });
}
